import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createOffer, getOffer, getOrderClients, updateOffer } from '../services/orders';
import ClientPicker from '../components/common/ClientPicker';
import OfferPage from './OfferPage';
import './OfferEditorPage.css';

/** LAKPED-16.3 — el editor de la oferta: una línea por artículo.
 *
 *  Lo único obligatorio es el precio. El importe de cada línea y el total los calcula también el
 *  servidor al guardar, así que lo que se ve aquí y lo que queda guardado no pueden discrepar.
 */
const PASOS = ['Documento', 'Histórico', 'Oferta', 'PDF y envío'];

const LINEA_VACIA = { part_code: '', description: '', quantity: '', unit_price: '', price_source: '' };

const numero = (valor) => (valor === '' || valor === null || valor === undefined ? null : Number(valor));
const euros = (valor) => (valor || valor === 0
  ? valor.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })
  : '—');

const importeDe = (linea) => {
  const precio = numero(linea.unit_price);
  if (precio === null || Number.isNaN(precio)) return null;
  const cantidad = numero(linea.quantity);
  return precio * (cantidad === null || Number.isNaN(cantidad) ? 1 : cantidad);
};

const OfferEditorPage = () => {
  const navigate = useNavigate();
  const { offerHash } = useParams();
  const [searchParams] = useSearchParams();
  const analysisHash = searchParams.get('analysis');

  const [cabecera, setCabecera] = useState({
    client: searchParams.get('cliente') || '',
    reference: '',
    notes: '',
  });
  const [lineas, setLineas] = useState([{
    ...LINEA_VACIA,
    part_code: searchParams.get('pieza') || '',
    price_source: searchParams.get('origen') || '',
  }]);
  const [clientes, setClientes] = useState([]);
  const [oferta, setOferta] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [guardada, setGuardada] = useState(false);
  // Las ofertas hechas con la calculadora de tiempos se siguen abriendo con su pantalla.
  const [usarCalculadora, setUsarCalculadora] = useState(false);

  useEffect(() => {
    getOrderClients().then(setClientes).catch(() => setClientes([]));
  }, []);

  const cargar = useCallback(async () => {
    if (!offerHash) return;
    try {
      const data = await getOffer(offerHash);
      if (!(data.lines || []).length && data.calculator) {
        setUsarCalculadora(true);
        return;
      }
      setOferta(data);
      setCabecera({ client: data.client?.name || '', reference: data.reference || '', notes: data.notes || '' });
      setLineas((data.lines || []).length
        ? data.lines.map(l => ({
            part_code: l.part_code || '',
            description: l.description || '',
            quantity: l.quantity ?? '',
            unit_price: l.unit_price ?? '',
            price_source: l.price_source || '',
          }))
        : [{ ...LINEA_VACIA }]);
    } catch (err) {
      setError('No se ha podido cargar la oferta.');
    }
  }, [offerHash]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarLinea = (indice, campo) => (e) => {
    const valor = e.target.value;
    setLineas(prev => prev.map((l, i) => (i === indice ? { ...l, [campo]: valor } : l)));
  };

  const quitarLinea = (indice) => setLineas(prev => (
    prev.length === 1 ? [{ ...LINEA_VACIA }] : prev.filter((_, i) => i !== indice)
  ));

  const total = lineas.reduce((suma, l) => suma + (importeDe(l) || 0), 0);
  const algunPrecio = lineas.some(l => importeDe(l) !== null);

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    const cuerpo = {
      reference: cabecera.reference || null,
      notes: cabecera.notes || null,
      lines: lineas
        .filter(l => l.part_code || l.description || l.unit_price !== '')
        .map(l => ({
          part_code: l.part_code || null,
          description: l.description || null,
          quantity: numero(l.quantity),
          unit_price: numero(l.unit_price),
          price_source: l.price_source || null,
        })),
    };
    try {
      if (offerHash) {
        await updateOffer(offerHash, cuerpo);
        setGuardada(true);
      } else {
        const creada = await createOffer({
          ...cuerpo,
          analysis_hash: analysisHash || null,
          client_name: cabecera.client || null,
        });
        navigate(`/offers/${creada.hash}`, { replace: true });
      }
    } catch (err) {
      setError('No se ha podido guardar la oferta.');
    } finally {
      setGuardando(false);
    }
  };

  if (usarCalculadora) return <OfferPage />;

  return (
    <div className="offer-editor">
      <ol className="offer-editor-pasos">
        {PASOS.map((titulo, i) => (
          <li key={titulo} className={i === 2 ? 'actual' : (i < 2 ? 'hecho' : '')}>
            <span className="offer-editor-paso-num">{i < 2 ? '✓' : i + 1}</span>
            {titulo}
          </li>
        ))}
      </ol>

      <h1>
        {oferta ? `Oferta ${oferta.reference || oferta.hash.slice(0, 8)}` : 'Nueva oferta'}
        <span className="offer-editor-estado">{oferta?.status === 'sent' ? 'Enviada' : 'Borrador'}</span>
      </h1>
      <p className="offer-editor-sub">Una línea por artículo. Lo único obligatorio es el precio.</p>

      {error && <p className="offer-editor-error">{error}</p>}
      {guardada && <p className="offer-editor-ok">Guardada.</p>}

      <div className="offer-editor-tarjeta">
        <h2>Datos</h2>
        <div className="offer-editor-datos">
          <label>
            Cliente
            <ClientPicker
              value={cabecera.client}
              clients={clientes}
              onChange={(valor) => setCabecera(prev => ({ ...prev, client: valor }))}
            />
          </label>
          <label>
            Referencia de la petición del cliente
            <input
              value={cabecera.reference}
              onChange={(e) => setCabecera(prev => ({ ...prev, reference: e.target.value }))}
              placeholder="RFQ-26-114"
            />
          </label>
        </div>
        {analysisHash && <p className="offer-editor-adjunto">Lleva enganchado el plano de la petición.</p>}
      </div>

      <div className="offer-editor-tarjeta">
        <h2>Líneas de la oferta</h2>
        <table className="offer-editor-tabla">
          <thead>
            <tr>
              <th>Artículo</th>
              <th>Descripción</th>
              <th className="der">Cant.</th>
              <th className="der">Precio ud. (€)</th>
              <th className="der">Importe</th>
              <th>Referencia de precio</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lineas.map((linea, i) => (
              <tr key={i}>
                <td><input value={linea.part_code} onChange={cambiarLinea(i, 'part_code')} /></td>
                <td><input value={linea.description} onChange={cambiarLinea(i, 'description')} /></td>
                <td className="der"><input className="corto" type="number" min="0" value={linea.quantity} onChange={cambiarLinea(i, 'quantity')} /></td>
                <td className="der"><input className="corto" type="number" min="0" step="0.01" value={linea.unit_price} onChange={cambiarLinea(i, 'unit_price')} /></td>
                <td className="der">{euros(importeDe(linea))}</td>
                <td>
                  <input
                    value={linea.price_source}
                    onChange={cambiarLinea(i, 'price_source')}
                    placeholder="de dónde sale el precio"
                  />
                </td>
                <td>
                  <button type="button" className="offer-editor-quitar" onClick={() => quitarLinea(i)} title="Quitar la línea">
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="offer-editor-pie-tabla">
          <button type="button" className="offer-editor-secundario" onClick={() => setLineas(prev => [...prev, { ...LINEA_VACIA }])}>
            + Añadir línea
          </button>
          <span className="offer-editor-total">
            Total (sin IVA) <b>{algunPrecio ? euros(total) : '—'}</b>
          </span>
        </div>
      </div>

      <div className="offer-editor-tarjeta">
        <h2>Condiciones</h2>
        <p className="offer-editor-ayuda">Plazo de entrega y observaciones. Aparecen en el PDF.</p>
        <textarea
          rows={3}
          value={cabecera.notes}
          onChange={(e) => setCabecera(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Plazo de entrega: 2 semanas desde recepción del material. Validez de la oferta: 30 días."
        />
      </div>

      <div className="offer-editor-acciones">
        <button type="button" className="offer-editor-secundario" onClick={() => navigate('/offers')}>
          Ver todas las ofertas
        </button>
        <button type="button" className="offer-editor-principal" onClick={guardar} disabled={guardando}>
          {guardando ? 'Guardando…' : 'Guardar borrador'}
        </button>
        {offerHash && (
          <button type="button" className="offer-editor-principal" onClick={() => navigate(`/offers/${offerHash}/pdf`)}>
            Vista previa del PDF
          </button>
        )}
      </div>

      <p className="offer-editor-nota">
        ¿Necesitas calcular el precio por tiempos de operación?{' '}
        <button type="button" className="offer-editor-enlace" onClick={() => navigate(`/offers/calculadora${analysisHash ? `?analysis=${analysisHash}` : ''}`)}>
          abrir la calculadora
        </button>
      </p>
    </div>
  );
};

export default OfferEditorPage;
